import { Component, For, Show, createMemo, createSignal } from "solid-js";
import type { ProjectMember } from "../../api/types";
import { Avatar } from "./Avatar";
import { Dropdown, DropdownItem } from "./Dropdown";
import { Spinner } from "./Spinner";

export interface UserPickerProps {
  members: ProjectMember[];
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
  disabled?: boolean;
  loading?: boolean;
  align?: "left" | "right";
}

const memberLabel = (m: ProjectMember): string =>
  m.user_name ?? m.user_email ?? m.user_id;

export const UserPicker: Component<UserPickerProps> = (props) => {
  const [search, setSearch] = createSignal("");

  const selectedMember = createMemo(() => {
    const id = props.selectedUserId;
    if (!id) return null;
    return props.members.find((m) => m.user_id === id) ?? null;
  });

  const filtered = createMemo(() => {
    const q = search().trim().toLowerCase();
    if (!q) return props.members;
    return props.members.filter((m) => {
      const haystack =
        `${m.user_name ?? ""} ${m.user_email ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  });

  const isInteractive = () => !props.disabled && !props.loading;

  const trigger = (
    <div
      onClick={(e) => {
        if (!isInteractive()) {
          e.stopPropagation();
        }
      }}
      class={isInteractive() ? "" : "cursor-not-allowed"}
    >
      <button
        type="button"
        aria-label="Assignee"
        aria-disabled={!isInteractive()}
        class={
          "inline-flex items-center gap-2 px-2 py-1 text-sm rounded border border-line bg-surface min-w-[10rem] " +
          (isInteractive()
            ? "hover:bg-sunken focus-ring"
            : "opacity-60 pointer-events-none")
        }
      >
        <Show
          when={!props.loading}
          fallback={<Spinner size="sm" class="text-ink-3" />}
        >
          <Show
            when={selectedMember()}
            fallback={<span class="text-ink-3">Unassigned</span>}
          >
            {(m) => (
              <>
                <Avatar
                  name={memberLabel(m())}
                  src={m().avatar_url}
                  size="sm"
                />
                <span class="text-ink truncate">{memberLabel(m())}</span>
              </>
            )}
          </Show>
        </Show>
        <svg
          class="ml-auto w-4 h-4 text-ink-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
    </div>
  );

  return (
    <Dropdown
      trigger={trigger}
      align={props.align}
      initialFocusSelector="[data-userpicker-search]"
    >
      <div class="px-2 pt-2 pb-1">
        <input
          data-userpicker-search
          type="text"
          class="w-full px-2 py-1.5 text-sm border border-line rounded bg-surface focus-ring"
          placeholder="Search members…"
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
          aria-label="Search members"
        />
      </div>
      <div class="max-h-64 overflow-y-auto">
        <DropdownItem onClick={() => props.onSelect(null)}>
          <span class="italic text-ink-2">Unassign</span>
        </DropdownItem>
        <Show when={filtered().length === 0}>
          <div class="px-3 py-2 text-sm text-ink-3">No members match</div>
        </Show>
        <For each={filtered()}>
          {(m) => (
            <DropdownItem onClick={() => props.onSelect(m.user_id)}>
              <div class="flex items-center gap-2">
                <Avatar name={memberLabel(m)} src={m.avatar_url} size="sm" />
                <span class="font-medium">{memberLabel(m)}</span>
                <Show when={m.user_email && m.user_email !== memberLabel(m)}>
                  <span class="text-xs text-ink-3 truncate">
                    {m.user_email}
                  </span>
                </Show>
              </div>
            </DropdownItem>
          )}
        </For>
      </div>
    </Dropdown>
  );
};
